import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useLang } from "@/i18n";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtK = (v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`);

const getTierIndex = (spend) => (spend < 1000 ? 0 : spend < 3000 ? 1 : spend < 10000 ? 2 : 3);

const endLabel = (text, color, dy = 4) => {
  const EndLabel = ({ x, y, index }) =>
    index === 12 ? (
      <text x={x + 10} y={y + dy} fontSize={11} fill={color} style={{ fontFamily: "Plus Jakarta Sans", fontWeight: 600 }}>
        {text}
      </text>
    ) : null;
  return <EndLabel />;
};

export const AwsCalculator = () => {
  const { t } = useLang();
  const [spend, setSpend] = useState(5000);
  const a = t.aws;

  const data = useMemo(() => {
    let current = 0, s3 = 0, s10 = 0, s30 = 0;
    return Array.from({ length: 13 }, (_, m) => {
      const monthly = spend * (0.9 + 0.05 * m);
      const ramp = Math.pow(m / 12, 1.1);
      current += monthly;
      s3 += monthly * 0.97;
      s10 += monthly * (1 - 0.1 * ramp);
      s30 += monthly * (1 - 0.3 * ramp);
      return { m, current: Math.round(current), s3: Math.round(s3), s10: Math.round(s10), s30: Math.round(s30) };
    });
  }, [spend]);

  const year3 = spend * 12 * 0.03;
  const yearLow = spend * 12 * 0.1;
  const yearHigh = spend * 12 * 0.3;
  const tierIdx = getTierIndex(spend);
  const fill = ((spend - 500) / (100000 - 500)) * 100;

  const legend = [
    { key: "current", label: a.chartCurrent, color: "#737373" },
    { key: "s3", label: a.chartS3, color: "#ff764a" },
    { key: "s10", label: a.chartS10, color: "#4361ee" },
    { key: "s30", label: a.chartS30, color: "#4361ee" },
  ];

  return (
    <div data-testid="aws-savings-calculator" className="rounded-3xl border border-black/10 bg-white p-6 sm:p-10 shadow-[0_24px_70px_rgba(30,30,56,0.08)]">
      <div className="font-jbmono text-[11px] uppercase tracking-[0.25em] text-[#ff764a] mb-3">{a.calcTitle}</div>
      <p className="text-[#999999] text-sm leading-relaxed mb-10 font-jakarta max-w-2xl">{a.calcSub}</p>

      <div className="flex flex-col lg:flex-row gap-10">
        <div className="flex-1 min-w-0">
          <label className="font-jbmono text-xs uppercase tracking-[0.2em] text-[#999999]">{a.calcChartTitle}</label>
          <div className="mt-3 font-cabinet text-4xl sm:text-5xl font-bold text-[#353535] tabular-nums" data-testid="aws-calculator-spend-display">
            {fmt(spend)}<span className="text-lg text-slate-500 font-normal">/mo</span>
          </div>
          <input
            data-testid="aws-calculator-slider"
            type="range"
            min={500}
            max={100000}
            step={500}
            value={spend}
            onChange={(e) => setSpend(Number(e.target.value))}
            className="calc-slider w-full mt-6"
            style={{ "--fill": `${fill}%` }}
          />
          <input
            data-testid="aws-calculator-input"
            type="number"
            min={500}
            max={100000}
            value={spend}
            onChange={(e) => setSpend(Math.min(100000, Math.max(500, Number(e.target.value) || 500)))}
            className="mt-4 w-40 rounded-lg bg-[#FAFAFD] border border-black/10 px-3 py-2 text-sm text-[#353535] font-jbmono outline-none focus:border-[#ff764a]"
          />

          <div data-testid="aws-savings-chart" className="mt-10 h-96 sm:h-[440px] -ml-4">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 20, right: 140, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="rgba(0,0,0,0.07)" vertical={false} />
                <XAxis
                  dataKey="m"
                  ticks={[0, 12]}
                  tickFormatter={(m) => (m === 0 ? a.chartToday : a.chartMonths)}
                  tick={{ fontSize: 11, fill: "#999999", fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={fmtK}
                  tick={{ fontSize: 11, fill: "#999999", fontFamily: "JetBrains Mono" }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                  label={{ value: a.chartYLabel, angle: -90, position: "insideLeft", offset: -42, style: { fontSize: 11, fill: "#999999", fontFamily: "Plus Jakarta Sans" } }}
                />
                <Tooltip
                  formatter={(v, name) => [fmt(v), legend.find((l) => l.key === name)?.label || name]}
                  labelFormatter={(m) => `${m === 0 ? a.chartToday : `${a.chartMonths} · m${m}`}`}
                  contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12, fontFamily: "Plus Jakarta Sans" }}
                />
                <Area type="monotone" dataKey="s10" stroke="none" fill="#4361ee" fillOpacity={0.12} />
                <Area type="monotone" dataKey="s30" stroke="none" fill="#ffffff" fillOpacity={1} />
                <Line type="monotone" dataKey="current" name="current" stroke="#737373" strokeWidth={2.5} dot={false} label={endLabel(a.chartCurrent, "#737373", -10)} />
                <Line type="monotone" dataKey="s3" name="s3" stroke="#ff764a" strokeWidth={2.5} dot={false} label={endLabel(a.chartS3, "#ff764a", 0)} />
                <Line type="monotone" dataKey="s10" name="s10" stroke="#4361ee" strokeWidth={2} dot={false} label={endLabel(a.chartS10, "#4361ee", 14)} />
                <Line type="monotone" dataKey="s30" name="s30" stroke="#4361ee" strokeWidth={2} dot={false} label={endLabel(a.chartS30, "#4361ee", 14)} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:w-[340px] shrink-0 rounded-2xl border border-black/10 bg-[#FAFAFD] p-6 sm:p-8 flex flex-col">
          <h3 className="font-cabinet text-2xl font-bold leading-tight">{a.calcPanelTitle}</h3>
          <div className="mt-7">
            <motion.div
              key={year3}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              data-testid="aws-savings-display"
              className="font-cabinet text-4xl font-bold text-[#ff764a] tabular-nums"
            >
              {fmt(year3)}
            </motion.div>
            <div className="mt-1.5 font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#999999]">{a.calcDirect3} · {a.calcPerYear}</div>
          </div>
          <div className="my-7 border-t border-black/8" />
          <p className="text-sm font-jakarta text-[#353535]/80 leading-relaxed">{a.calcExtraText}</p>
          <motion.div
            key={`${yearLow}-${yearHigh}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="aws-savings-extra"
            className="mt-3 font-cabinet text-3xl font-bold text-[#4361ee] tabular-nums"
          >
            {fmt(yearLow)} – {fmt(yearHigh)}
          </motion.div>
          <div className="mt-1.5 font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#999999]">{a.calcPerYear}</div>
          <Link
            to="/contact?unit=aws&service=aws-reselling"
            data-testid="aws-calculator-cta"
            className="group mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#ff764a] px-7 py-3.5 text-sm font-jakarta font-bold text-[#353535] transition-all duration-300 hover:gap-4 hover:shadow-[0_10px_35px_rgba(255,118,74,0.35)]"
          >
            {a.calcCta}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <p className="mt-5 text-[11px] text-slate-400 font-jakarta">{a.calcNote}</p>
        </div>
      </div>

      <div className="mt-12">
        <div className="font-jbmono text-xs uppercase tracking-[0.2em] text-[#999999] mb-5">{a.calcTier}</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {a.tiers.map((tr, i) => (
            <div
              key={i}
              data-testid={`aws-tier-card-${i}`}
              className={`rounded-2xl border p-5 transition-all duration-500 ${
                i === tierIdx ? "border-[#ff764a] bg-[#ff764a]/5 shadow-[0_16px_40px_rgba(255,118,74,0.12)]" : "border-black/10 bg-white"
              }`}
            >
              <div className={`font-cabinet text-lg font-bold ${i === tierIdx ? "text-[#ff764a]" : "text-[#353535]"}`}>{tr.name}</div>
              <div className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-[#999999]">{tr.range}</div>
              <ul className="mt-4 flex flex-col gap-2">
                {tr.perks.map((perk, j) => (
                  <li key={j} className="flex items-start gap-2 text-xs text-slate-600 font-jakarta leading-relaxed">
                    <Check size={12} className="mt-0.5 shrink-0 text-[#0d9488]" /> {perk}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
